import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Form } from "antd";
import { useTranslation } from "react-i18next";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import { JPInputSelectProps } from "./jpInputSelect.model";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import "@companion-core/web/src/Assets/Styles/scss/inputselect.scss";

export const JPInputSelect: React.FC<JPInputSelectProps> = ({
  name,
  payloadPath = name,
  required = false,
  bordered = true,
  disabled = false,
  icon,
  label,
  ariaLabel,
  ariaLabelledBy,
  labelRender,
  placeholder,
  initialValue = undefined,
  options,
  testID,
  dropdownClass,
  setFieldErrors,
  onValueChange,
  registerValidation,
}: JPInputSelectProps) => {
  const form = Form.useFormInstance(); // Get current context form instance
  const { t } = useTranslation();
  const [inputValue, setValue] = useState<string | undefined>(initialValue);
  const inputValueRef = useRef(inputValue); // Create a ref to store the latest value and avoid stale closure

  const [error, setError] = useState<string>("");

  // transform each locolize keys to string value
  const translatedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        label: t(option.text),
      })),
    [options, t]
  );

  useMountEffect(() => {
    registerValidation?.(name, validateInput);
    // The parent only learns the value through onValueChange: without this, a pre-selected option
    // is displayed but never reaches the form payload.
    if (initialValue !== undefined && initialValue !== null) onValueChange?.(initialValue);
  });

  useEffect(() => {
    inputValueRef.current = inputValue; // Update ref value
  }, [inputValue]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: inputValue,
      });
    }
  }, [payloadPath, form, inputValue]);

  const validateInput = () => {
    const error = !inputValueRef.current && required ? "common.form.input.error.required" : "";
    setError(error);
    setFieldErrors && setFieldErrors(error ? [error] : []);
  };

  const handleOnChange = (value: string) => {
    if (value) {
      setError("");
    }
    setValue(value);
    onValueChange?.(value);
  };

  const inputId = `${name}-select`;
  const errorId = `${name}-error`;

  return (
    <div className="JPInputSelect">
      {label && (
        <div className="JPInputSelect__label">
          <InputLabel label={label} icon={icon} htmlFor={inputId} id={`${name}-select-label`} />
        </div>
      )}
      <Select
        id={inputId}
        popupRender={(menu) => (
          <div className={`JPInputSelect__dropdown ${dropdownClass}`}>{menu}</div>
        )}
        className="JPInputSelect__input"
        data-testid={testID}
        variant={bordered ? "outlined" : "borderless"}
        options={translatedOptions}
        labelRender={labelRender}
        status={error ? "error" : undefined}
        value={inputValue}
        size="large"
        disabled={disabled}
        classNames={{ popup: { root: "pop-up" } }}
        suffixIcon={<FontIcon name="JP-Arrow-down-bold" size={14} aria-hidden="true" />}
        placeholder={<span className="placeholder body-text-6">{placeholder}</span>}
        onBlur={validateInput}
        onChange={(value) => handleOnChange(value)}
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      />
      {error && <JPInputError inputLabel={label} testID={testID} error={error} id={errorId} />}
    </div>
  );
};
